import React, { useState } from 'react';
import {
  Box, Button, CircularProgress, Divider, Grid, Paper, Tab, Tabs, TextField, Typography,
} from '@material-ui/core';

import { makeStyles } from '@material-ui/core/styles';

import {
  MainDispatchFunc,
  RequestUpdatePasswordArgs,
  RequestUpdateProfileArgs,
  UpdateProfileFieldArgs,
  UserProfile,
} from '../../types';

import styles from './SettingsPopup.styles';
import PopupDialog from '../../../../../components/popupDialog';
import { capitalizeCamel } from '../../../../../core/utils/stringUtils';
import FormField from '../../../../../components/formField';
import { FieldUpdateArgs } from '../../../../../core/types/form';
import NetworkState from '../../../../../core/types/network';
import { LoginInfo } from '../../../login/types';

type Props = {
  settingsPopup: {
    isOpen: boolean;
    userProfile?: UserProfile;
  };
  loginStatus?: LoginInfo;
  closeSettingsPopup: MainDispatchFunc<unknown>;
  requestUpdateProfile: MainDispatchFunc<RequestUpdateProfileArgs>;
  updateProfileField: MainDispatchFunc<UpdateProfileFieldArgs>;
  requestUpdatePassword: MainDispatchFunc<RequestUpdatePasswordArgs>;
  networkStates: {
    updateProfileRequest: NetworkState;
    updatePasswordRequest: NetworkState;
  };
};

const useStyles = makeStyles(styles);
const SettingsPopup: React.FC<Props> = ({
  settingsPopup: {
    isOpen,
    userProfile,
  },
  loginStatus,
  closeSettingsPopup,
  requestUpdateProfile,
  updateProfileField,
  requestUpdatePassword,
  networkStates: {
    updateProfileRequest,
    updatePasswordRequest,
  },
}: Props) => {
  const classes = useStyles();
  type SettingsTabHeader = 'profile' | 'password';
  const [selectedTab, setSelectedTab] = useState<SettingsTabHeader>(('profile') as SettingsTabHeader);

  const tabs: SettingsTabHeader[] = [
    'profile',
    'password',
  ];
  const tabIndex = tabs.indexOf(selectedTab);

  const checkPasswordMatch = () => {
    if (!userProfile?.userNewPassword || !userProfile?.userRePassword) {
      return false;
    }

    if (userProfile?.userNewPassword !== userProfile?.userRePassword) {
      return false;
    }

    return true;
  };

  const canSavePassword = () => {
    if (userProfile?.userCurrentPassword === '' || userProfile?.userNewPassword === '' || userProfile?.userRePassword === '') {
      return true;
    }

    if (!checkPasswordMatch()) {
      return true;
    }

    if (userProfile?.userNewPassword && userProfile?.userRePassword) {
      if (userProfile?.userNewPassword.length < 4 || userProfile?.userRePassword.length < 4) {
        return true;
      }
    }

    return false;
  };

  return (
    <PopupDialog
      open={isOpen}
      title="Settings"
      closeAction={closeSettingsPopup}
      maxWidth="lg"
      classes={{
        content: classes.root,
      }}
    >
      <Box className={classes.details}>
        <Tabs
          indicatorColor="secondary"
          textColor="primary"
          value={tabIndex}
          scrollButtons="on"
          variant="scrollable"
          onChange={(_v, index) => {
            const tab = tabs[index];
            setSelectedTab(tab);
          }}
        >
          <Tab label={capitalizeCamel(tabs[0])} classes={{ root: classes.tabItem }} />
          <Tab label={capitalizeCamel(tabs[1])} classes={{ root: classes.tabItem }} />
        </Tabs>
        <Divider />
        <Box>
          {selectedTab === 'profile' && (
            <>
              {loginStatus?.data?.mode === 'place' && (
                <Paper classes={{ root: classes.paper }}>
                  <Grid item container spacing={2}>
                    <Grid item container xs={12} sm={6} alignItems="flex-end">
                      <FormField
                        fieldType="input"
                        className={classes.formControl}
                        label="Username"
                        value={userProfile?.username}
                        isReadOnly
                      />
                    </Grid>
                    <Grid item container xs={12} sm={6} alignItems="flex-end">
                      <Typography color="primary" variant="body1">
                        {userProfile?.userType === 'all' ? 'All access' : 'View Only'}
                      </Typography>
                    </Grid>
                    <Grid item container xs={12} sm={2} alignItems="flex-end">
                      <FormField
                        fieldType="input"
                        className={classes.formControl}
                        label="Pre name"
                        value={userProfile?.userPersonName?.frontName}
                        updateAction={(args: FieldUpdateArgs) => updateProfileField({
                          path: 'userPersonName.frontName',
                          value: args.value,
                        })}
                      />
                    </Grid>
                    <Grid item container xs={12} sm={5} alignItems="flex-end">
                      <FormField
                        fieldType="input"
                        className={classes.formControl}
                        label="First name"
                        value={userProfile?.userPersonName?.firstName}
                        updateAction={(args: FieldUpdateArgs) => updateProfileField({
                          path: 'userPersonName.firstName',
                          value: args.value,
                        })}
                      />
                    </Grid>
                    <Grid item container xs={12} sm={5} alignItems="flex-end">
                      <FormField
                        fieldType="input"
                        className={classes.formControl}
                        label="Last name"
                        value={userProfile?.userPersonName?.lastName}
                        updateAction={(args: FieldUpdateArgs) => updateProfileField({
                          path: 'userPersonName.lastName',
                          value: args.value,
                        })}
                      />
                    </Grid>
                    <Grid item container xs={12} sm={6} alignItems="flex-end">
                      <FormField
                        fieldType="input"
                        className={classes.formControl}
                        label="Position / Division"
                        value={userProfile?.userPosition}
                        updateAction={(args: FieldUpdateArgs) => updateProfileField({
                          path: 'userPosition',
                          value: args.value,
                        })}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {loginStatus?.data?.mode === 'person' && (
                <Paper classes={{ root: classes.paper }}>
                  <Grid item container spacing={2}>
                    <Grid item container xs={12} sm={6} alignItems="flex-end">
                      <FormField
                        fieldType="input"
                        className={classes.formControl}
                        label="Citizen ID."
                        value={userProfile?.username}
                        isReadOnly
                      />
                    </Grid>
                    <Grid item container xs={12} sm={6} alignItems="flex-end">
                      <Typography color="primary" variant="body1">
                        {userProfile?.userType === 'all' ? 'All access' : 'View Only'}
                      </Typography>
                    </Grid>
                    <Grid item container xs={12} sm={2} alignItems="flex-end">
                      <FormField
                        fieldType="input"
                        className={classes.formControl}
                        label="Pre name"
                        value={userProfile?.userPersonName?.frontName}
                        isReadOnly
                        updateAction={(args: FieldUpdateArgs) => updateProfileField({
                          path: 'userPersonName.frontName',
                          value: args.value,
                        })}
                      />
                    </Grid>
                    <Grid item container xs={12} sm={5} alignItems="flex-end">
                      <FormField
                        fieldType="input"
                        className={classes.formControl}
                        label="First name"
                        value={userProfile?.userPersonName?.firstName}
                        isReadOnly
                        updateAction={(args: FieldUpdateArgs) => updateProfileField({
                          path: 'userPersonName.firstName',
                          value: args.value,
                        })}
                      />
                    </Grid>
                    <Grid item container xs={12} sm={5} alignItems="flex-end">
                      <FormField
                        fieldType="input"
                        className={classes.formControl}
                        label="Last name"
                        value={userProfile?.userPersonName?.lastName}
                        isReadOnly
                        updateAction={(args: FieldUpdateArgs) => updateProfileField({
                          path: 'userPersonName.lastName',
                          value: args.value,
                        })}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              )}

              <Box className={classes.command}>
                <Button
                  color="default"
                  classes={{ root: classes.actionButton }}
                  variant="text"
                  size="medium"
                  onClick={() => closeSettingsPopup()}
                  disabled={updateProfileRequest.isRequesting}
                >
                  Close
                </Button>
                {loginStatus?.data?.mode === 'place' && (
                  <Button
                    color="secondary"
                    classes={{ root: classes.actionButton }}
                    variant="contained"
                    size="medium"
                    onClick={() => requestUpdateProfile({
                      userId: userProfile?.userId!,
                      username: userProfile?.username!,
                      userPersonName: userProfile?.userPersonName,
                      userPosition: userProfile?.userPosition,
                      userType: userProfile?.userType,
                    })}
                    disabled={updateProfileRequest.isRequesting}
                  >
                    {updateProfileRequest.isRequesting ? (
                      <CircularProgress size={24} className={classes.buttonProgress} />
                    ) : 'Save'}
                  </Button>
                )}
              </Box>
            </>
          )}
          {selectedTab === 'password' && (
            <>
              <Paper classes={{ root: classes.paper }}>
                <Grid item container spacing={2}>
                  <Grid item container xs={12} alignItems="flex-end">
                    <TextField
                      label="Current Password"
                      variant="standard"
                      size="medium"
                      required
                      type="password"
                      className={classes.formControlText}
                      value={userProfile?.userCurrentPassword || ''}
                      onChange={(event) => updateProfileField({
                        path: 'userCurrentPassword',
                        value: event.target.value,
                      })}
                      disabled={updatePasswordRequest.isRequesting}
                    />
                  </Grid>
                  <Grid item container xs={12} alignItems="flex-end">
                    <TextField
                      label="New Password"
                      variant="standard"
                      size="medium"
                      required
                      type="password"
                      className={classes.formControlText}
                      value={userProfile?.userNewPassword || ''}
                      onChange={(event) => updateProfileField({
                        path: 'userNewPassword',
                        value: event.target.value,
                      })}
                      disabled={updatePasswordRequest.isRequesting}
                      error={!checkPasswordMatch()
                        && (userProfile?.userNewPassword || '').length >= 4
                        && (userProfile?.userRePassword || '').length >= 4}
                    />
                  </Grid>
                  <Grid item container xs={12} alignItems="flex-end">
                    <TextField
                      label="Re-New Password"
                      variant="standard"
                      size="medium"
                      required
                      type="password"
                      className={classes.formControlText}
                      value={userProfile?.userRePassword || ''}
                      onChange={(event) => updateProfileField({
                        path: 'userRePassword',
                        value: event.target.value,
                      })}
                      disabled={updatePasswordRequest.isRequesting}
                      error={!checkPasswordMatch()
                        && (userProfile?.userNewPassword || '').length >= 4
                        && (userProfile?.userRePassword || '').length >= 4}
                    />
                  </Grid>
                </Grid>
              </Paper>
              <Box className={classes.command}>
                <Button
                  color="default"
                  classes={{ root: classes.actionButton }}
                  variant="text"
                  size="medium"
                  onClick={() => closeSettingsPopup()}
                  disabled={updatePasswordRequest.isRequesting}
                >
                  Close
                </Button>
                {!checkPasswordMatch()
                && (userProfile?.userNewPassword || '').length >= 4
                && (userProfile?.userRePassword || '').length >= 4
                && (
                  <Typography color="error" variant="body2" style={{ alignSelf: 'center' }}>
                    Password Not Match
                  </Typography>
                )}
                <Button
                  color="secondary"
                  classes={{ root: classes.actionButton }}
                  variant="contained"
                  size="medium"
                  onClick={() => requestUpdatePassword({
                    userId: userProfile?.userId!,
                    username: userProfile?.username!,
                    userCurrentPassword: userProfile?.userCurrentPassword,
                    userNewPassword: userProfile?.userNewPassword,
                    mode: loginStatus?.data?.mode!,
                  })}
                  disabled={canSavePassword() || updatePasswordRequest.isRequesting}
                >
                  {updatePasswordRequest.isRequesting ? (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  ) : 'Save'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </PopupDialog>
  );
};

export default SettingsPopup;
